import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useStream } from './stream'

export interface ArticleAlert {
  id: number
  headline: string
  url?: string
  publishedAt?: string
  receivedAt: Date
}

type AlertContextValue = {
  enabled: boolean
  permission: NotificationPermission | 'unsupported'
  alert?: ArticleAlert
  unread: number
  filterExpression: string
  similarityThreshold: number
  setFilterExpression: (value: string) => void
  setSimilarityThreshold: (value: number) => void
  enable: () => Promise<void>
  test: () => Promise<void>
  dismiss: () => void
}

const AlertContext = createContext<AlertContextValue | null>(null)
const baseTitle = 'Blaze — News Intelligence'

export function AlertProvider({ children }: { children: ReactNode }) {
  const { events } = useStream()
  const [enabled, setEnabled] = useState(() => localStorage.getItem('blaze.article-alerts') === 'enabled')
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => 'Notification' in window ? Notification.permission : 'unsupported')
  const [alert, setAlert] = useState<ArticleAlert>()
  const [unread, setUnread] = useState(0)
  const [filterExpression, setFilterExpressionState] = useState(() => localStorage.getItem('blaze.article-alert-filter') ?? '')
  const [similarityThreshold, setSimilarityThresholdState] = useState(() => Number(localStorage.getItem('blaze.article-alert-similarity') ?? 0.65))
  const handledID = useRef(0)
  const audioContext = useRef<AudioContext | null>(null)
  const latest = events[0]
  const latestID = latest?.id ?? 0

  const playTone = useCallback(async (force = false) => {
    if (!enabled && !force) return
    const Context = window.AudioContext ?? window.webkitAudioContext
    if (!Context) return
    const audio = audioContext.current ?? new Context()
    audioContext.current = audio
    await audio.resume()
    const start = audio.currentTime
    for (const [index, frequency] of [740, 920, 740].entries()) {
      const oscillator = audio.createOscillator()
      const gain = audio.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, start + index * 0.16)
      gain.gain.exponentialRampToValueAtTime(0.12, start + index * 0.16 + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + index * 0.16 + 0.12)
      oscillator.connect(gain).connect(audio.destination)
      oscillator.start(start + index * 0.16)
      oscillator.stop(start + index * 0.16 + 0.13)
    }
  }, [enabled])

  useEffect(() => {
    if (!latest || latestID <= handledID.current || latest.type !== 'article.new') return
    handledID.current = latestID
    const data = latest.data && typeof latest.data === 'object' ? latest.data as Record<string, unknown> : {}
    if (data.baseline_import === true) return
    const headline = String(data.headline ?? 'A followed source published a new article')
    if (!matchesAlertFilter(headline, filterExpression, similarityThreshold)) return
    const incoming: ArticleAlert = {
      id: latestID, headline, url: typeof data.url === 'string' ? data.url : undefined,
      publishedAt: typeof data.published_at === 'string' ? data.published_at : undefined, receivedAt: latest.receivedAt,
    }
    setAlert(incoming)
    setUnread((count) => count + 1)
    document.title = `🔴 NEW • ${headline}`
    const theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    theme?.setAttribute('content', '#df2b23')
    void playTone()
    if (enabled && permission === 'granted') {
      const notification = new Notification('Blaze · New article', { body: headline, tag: `blaze-article-${latestID}`, requireInteraction: true })
      notification.onclick = () => { window.focus(); notification.close() }
    }
  }, [latestID, latest, enabled, permission, playTone, filterExpression, similarityThreshold])

  const setFilterExpression = useCallback((value: string) => {
    setFilterExpressionState(value)
    localStorage.setItem('blaze.article-alert-filter', value)
  }, [])

  const setSimilarityThreshold = useCallback((value: number) => {
    const normalized = Math.min(1, Math.max(0, value))
    setSimilarityThresholdState(normalized)
    localStorage.setItem('blaze.article-alert-similarity', String(normalized))
  }, [])

  const enable = useCallback(async () => {
    setEnabled(true)
    localStorage.setItem('blaze.article-alerts', 'enabled')
    const Context = window.AudioContext ?? window.webkitAudioContext
    if (Context) {
      const audio = audioContext.current ?? new Context()
      audioContext.current = audio
      await audio.resume()
    }
    if ('Notification' in window) setPermission(await Notification.requestPermission())
  }, [])

  const test = useCallback(async () => {
    setEnabled(true)
    localStorage.setItem('blaze.article-alerts', 'enabled')
    const permissionPromise = 'Notification' in window ? Notification.requestPermission() : Promise.resolve<'unsupported'>('unsupported')
    await playTone(true)
    const nextPermission = await permissionPromise
    setPermission(nextPermission)
    const sample: ArticleAlert = { id: -1, headline: 'Test alert — new-article notifications are ready', receivedAt: new Date() }
    setAlert(sample)
    document.title = `🔴 TEST • ${sample.headline}`
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', '#df2b23')
    if (nextPermission === 'granted') {
      const notification = new Notification('Blaze · Test alert', { body: 'Short sound and browser notifications are ready.', tag: 'blaze-alert-test' })
      notification.onclick = () => { window.focus(); notification.close() }
    }
  }, [playTone])

  const dismiss = useCallback(() => {
    setAlert(undefined)
    setUnread(0)
    document.title = baseTitle
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', '#0c0d0d')
  }, [])

  const value = useMemo(() => ({ enabled, permission, alert, unread, filterExpression, similarityThreshold, setFilterExpression, setSimilarityThreshold, enable, test, dismiss }),
    [enabled, permission, alert, unread, filterExpression, similarityThreshold, setFilterExpression, setSimilarityThreshold, enable, test, dismiss])
  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}

export function matchesAlertFilter(headline: string, expression: string, similarityThreshold: number) {
  const query = expression.trim()
  if (!query) return true
  const headlineText = normalizeFilterText(headline)
  const groups = query.split(/\s+OR\s+/i)
  return groups.some((group) => {
    const tokens = group.match(/"[^"]+"|\bNOR\b|\bNOT\b|\bAND\b|[^\s]+/gi) ?? []
    const required: string[] = []
    const excluded: string[] = []
    let negate = false
    for (const token of tokens) {
      if (/^AND$/i.test(token)) continue
      if (/^(?:NOT|NOR)$/i.test(token)) { negate = true; continue }
      const term = normalizeFilterText(token.replace(/^"|"$/g, ''))
      if (!term) continue
      ;(negate ? excluded : required).push(term)
      negate = false
    }
    if (excluded.some((term) => headlineText.includes(term))) return false
    if (!required.length) return false
    return required.every((term) => headlineText.includes(term) || tokenSimilarity(headlineText, term) >= similarityThreshold)
  })
}

function normalizeFilterText(value: string) { return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim() }
function tokenSimilarity(left: string, right: string) {
  const leftTokens = [...new Set(left.split(' ').filter(Boolean))]
  const rightTokens = [...new Set(right.split(' ').filter(Boolean))]
  if (!rightTokens.length || !leftTokens.length) return 0
  let score = 0
  for (const rightToken of rightTokens) {
    let best = 0
    for (const leftToken of leftTokens) best = Math.max(best, wordSimilarity(leftToken, rightToken))
    score += best
  }
  return score / rightTokens.length
}

function wordSimilarity(left: string, right: string) {
  if (left === right) return 1
  const longest = Math.max(left.length, right.length)
  if (longest < 4) return 0
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
    let diagonal = previous[0]
    previous[0] = leftIndex
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
      const above = previous[rightIndex]
      previous[rightIndex] = Math.min(previous[rightIndex] + 1, previous[rightIndex - 1] + 1, diagonal + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1))
      diagonal = above
    }
  }
  return 1 - previous[right.length] / longest
}

export function useArticleAlerts() {
  const value = useContext(AlertContext)
  if (!value) throw new Error('useArticleAlerts must be used inside AlertProvider')
  return value
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}
